import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getColors, getGroupColors } from '@store/design/action';
import { getAllColors, getAllGroupColors } from '@store/reducers';

const SelectColor = ({
	selectedColorList,
	setSelectedColorList,
	setPantoneColorIds,
	isShowLabel = true,
	isMultiple = true,
	placeholder = '',
	colorType = 'pantone',
	selectedColorType,
	isDisabled = false
}) => {
	const [searchColor, setSearchColor] = useState('');
	const [showColorDropdown, setShowColorDropdown] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const dispatch = useDispatch();
	const dropdownRef = useRef();
	const searchInputRef = useRef();
	const dropdownListRef = useRef();

	const onOutSideClick = (e) => {
		let selectedElement = e.target;
		if (dropdownRef.current && !dropdownRef.current.contains(selectedElement)) {
			setShowColorDropdown(false);
			setSearchColor('');
			setFocusedIndex(-1);
		}
	};

	useEffect(() => {
		document.addEventListener('click', onOutSideClick, { capture: true });
		return () => {
			document.removeEventListener('click', onOutSideClick, { capture: true });
		};
	}, []);

	useEffect(() => {
		if (showColorDropdown && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [showColorDropdown]);

	const colors = useSelector(getAllColors).map((item) => ({
		id: item.id,
		pantoneColorId: item.id,
		name: item.name,
		code: item.code,
		hexCode: item.hexCode
	}));

	const groupColors = useSelector(getAllGroupColors)
		.map((item) => ({
			id: item.id,
			name: item.name,
			hexCode: item.hexCode,
			isTransparent: item.isTransparent
		}))
		.filter((item) => {
			if (selectedColorType !== 'SOLID') {
				return !item.isTransparent;
			}
			return true;
		});

	const getFilteredColors = () => {
		const sourceColors = colorType === 'pantone' ? colors : groupColors;
		return sourceColors.filter(
			(color) =>
				!selectedColorList?.some((selectedColor) => color?.id === selectedColor?.id) &&
				(color?.name?.toLowerCase()?.includes(searchColor?.toLowerCase()) ||
					color?.code?.toLowerCase()?.includes(searchColor?.toLowerCase()))
		);
	};

	useEffect(() => {
		if (colorType === 'pantone') {
			dispatch(getColors(searchColor));
		}
	}, [dispatch, searchColor, colorType]);

	useEffect(() => {
		if (colorType === 'group') {
			dispatch(getGroupColors());
		}
	}, [dispatch, colorType]);

	useEffect(() => {
		let pantoneColorIds = selectedColorList?.map((colorItem) => colorItem?.id);
		setPantoneColorIds(pantoneColorIds);
	}, [selectedColorList]);

	const onColorClicks = (item) => {
		if (!isMultiple) {
			setSelectedColorList([item]);
			setShowColorDropdown(false);
		} else {
			setSelectedColorList([...selectedColorList, item]);
		}
		setSearchColor('');
	};

	const onRemoveColor = (item) => {
		setSelectedColorList([...selectedColorList.filter((color) => color?.id !== item?.id)]);
	};

	useEffect(() => {
		if (focusedIndex >= 0 && dropdownListRef.current) {
			const focusedElement = dropdownListRef.current.children[focusedIndex];
			if (focusedElement) {
				focusedElement.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest'
				});
			}
		}
	}, [focusedIndex]);

	const handleKeyDown = (e) => {
		const filteredColors = getFilteredColors();

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setFocusedIndex((prev) => Math.min(prev + 1, filteredColors.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setFocusedIndex((prev) => Math.max(prev - 1, -1));
		} else if (e.key === 'Enter' && focusedIndex >= 0) {
			e.preventDefault();
			onColorClicks(filteredColors[focusedIndex]);
		}
	};

	return (
		<div className="color-select-dropdown select-component chip-component mb-3" ref={dropdownRef}>
			{isShowLabel && <label htmlFor="collection">Color</label>}
			<div
				className={`click-box color-click-box cursor-pointer text-right click mb-1 ${isDisabled ? 'disabled' : ''}`}
				onClick={() => !isDisabled && setShowColorDropdown((prev) => !prev)}
			>
				<div className="all-color-code">
					<ul className="d-flex gap-1 flex-wrap">
						{selectedColorList?.length === 0 && (
							<span className="gray_dark_02 regular-14">{placeholder}</span>
						)}
						{selectedColorList?.map((item) => (
							<li
								className="d-flex justify-content-start align-items-center"
								key={`key_${item?.id}`}
							>
								<span
									className="color-icon mr-1"
									style={{
										background: item?.hexCode
									}}
								/>
								<span className="code gray_dark_02 regular-14">
									{item?.code || item?.name}
								</span>
								{colorType === 'pantone' && (
									<span className="gray_dark_02 regular-14">, {item?.name}</span>
								)}
							</li>
						))}
					</ul>
				</div>
				<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} />
			</div>
			<div className={`dropdown-box dropdown-color ${showColorDropdown ? 'show' : ''}`}>
				<div className="p-2">
					{selectedColorList?.map((item) => (
						<div className="select-color-item mb-2" key={`key_${item?.id}`}>
							<div className="single-item d-flex justify-content-between align-items-center align-self-center">
								<div className="color-and-title d-flex justify-content-start align-items-center">
									<div className="color-bg">
										<span
											className="color-icon d-flex"
											style={{
												background: item?.hexCode
											}}
										/>
									</div>
									<span className="color-code-no gray_dark_02 regular-14">
										{item?.code || item?.name}
									</span>
									{colorType === 'pantone' && (
										<span className="gray_dark_02 regular-14">, {item?.name}</span>
									)}
								</div>
								<span
									className="close-color cursor-pointer"
									onClick={() => onRemoveColor(item)}
								>
									<img src={`${process.env.PUBLIC_URL}/icons/minus-gray.svg`} width={20} />
								</span>
							</div>
						</div>
					))}
				</div>

				<div className="search-filter pt-1">
					<div className="with-icon right-icon">
						<input
							type="text"
							placeholder="Search"
							onChange={(e) => {
								setSearchColor(e.target.value);
							}}
							value={searchColor}
							ref={searchInputRef}
							onKeyDown={handleKeyDown}
						/>
						<span className="right-icon mt-1">
							<img src={`${process.env.PUBLIC_URL}/icons/search.png`} />
						</span>
					</div>
				</div>
				<div className="color-list-box color-add-box show scroll-y-label" ref={dropdownListRef}>
					{getFilteredColors()?.map((item, index) => (
						<div
							className={`single-color-item d-flex multi-color-box ${
								colorType === 'group' ? 'group-color' : ''
							} ${index === focusedIndex ? 'selected' : ''}`}
							key={item.id}
							onClick={() => onColorClicks(item)}
						>
							<div className="one-half color-code multi-color-box">
								<span
									className="color-icon multi-color-box"
									style={{
										background: item?.hexCode
									}}
								/>
								<span className="regular-14 text-nowrap">{item.code}</span>
							</div>
							<div className="one-half color-name multi-color-box">
								<p className="regular-14">{item.name}</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default SelectColor;
